import type { ReactNode } from "react";
import type { RenderContext } from "./context";
import { domId } from "./context";
import { cx } from "./ui";

/**
 * Visitor forms (wishes, RSVP). The engine renders plain HTML only: on live sites the web runtime's
 * responses script submits them to /__baze/* and fills lists; in the editor they are inert.
 */

export const FIELD =
  "block w-full rounded-site-md border border-site-border bg-site-bg px-4 py-3 text-[0.9375rem] text-site-text placeholder:text-site-muted/70 transition-colors focus:border-site-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-site-primary/30 disabled:opacity-60 motion-reduce:transition-none";

export const LABEL = "mb-1.5 block text-sm font-semibold text-site-text";

export function FormField({ ctx, id, label, children }: { ctx: RenderContext; id: string; label: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={domId(ctx, id)} className={LABEL}>
        {label}
      </label>
      {children}
    </div>
  );
}

/** Spam trap: hidden from people and assistive tech; bots that fill it are answered but nothing is stored. */
export function Honeypot() {
  return (
    // Inline style, not utility classes: the trap must stay off-screen even if the stylesheet fails to load.
    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: 1, height: 1, overflow: "hidden" }}>
      <label>
        Website
        <input type="text" name="website" tabIndex={-1} autoComplete="off" defaultValue="" />
      </label>
    </div>
  );
}

/** Where the runtime writes "Terima kasih" or an error. */
export function FormStatus() {
  return <p data-baze-status="" role="status" aria-live="polite" className="text-sm font-medium text-site-muted empty:hidden" />;
}

export const ATTENDANCE: ReadonlyArray<readonly ["yes" | "no" | "maybe", string]> = [
  ["yes", "Hadir"],
  ["no", "Tidak hadir"],
  ["maybe", "Masih ragu"],
];

/** Segmented radio group (Hadir / Tidak hadir / Masih ragu). */
export function AttendanceChoice({ ctx, name, idBase, required, disabled }: { ctx: RenderContext; name: string; idBase: string; required?: boolean; disabled?: boolean }) {
  return (
    <fieldset>
      <legend className={LABEL}>Kehadiran</legend>
      <div className="grid grid-cols-3 gap-2">
        {ATTENDANCE.map(([value, label]) => {
          const id = domId(ctx, `${idBase}-${value}`);
          return (
            <div key={value}>
              <input type="radio" id={id} name={name} value={value} required={required} disabled={disabled} className="peer sr-only" />
              <label
                htmlFor={id}
                className={cx(
                  "flex min-h-11 cursor-pointer items-center justify-center rounded-site-md border border-site-border bg-site-bg px-2 text-center text-sm font-semibold text-site-text transition-colors motion-reduce:transition-none",
                  "peer-checked:border-site-primary peer-checked:bg-site-primary peer-checked:text-site-on-primary peer-focus-visible:ring-2 peer-focus-visible:ring-site-primary/30",
                )}
              >
                {label}
              </label>
            </div>
          );
        })}
      </div>
    </fieldset>
  );
}
